import PropTypes from "prop-types";
import React, { useState } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import Flatpickr from "react-flatpickr";
import { confirmDateTimePickerProps } from "../../../../shared/confirmDateTimePicker";
import RoomSelector from "../../../../shared/RoomSelector";
import EventTemplateSelect from "../../../../shared/EventTemplateSelect";
import { offsetDateForBrowser, offsetDateForServer } from "../../../../shared/timeZoneLogic";
import { toastError, toastSuccess } from "../../../../shared/toastHelper";
import { canSubmitHoldConfirm } from "./holdActionValidation";
import { applyDefaultStart, resolveTimeDefaults } from "../../../../shared/eventTimeDefaults";

import moment from "moment";

const flatPickrFormat = "n/j/y at h:iK";

const holdToConfirmModalContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "8px",
  boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  width: "594px",
  padding: "0px",
};

export const buildSelectedRooms = (rooms, roomIds) =>
  (rooms || [])
    .filter((r) => (roomIds || []).includes(r.id))
    .map((r) => ({ value: r.id, label: r.name, room: r, venueOwnershipId: r.venue_ownership_id }));

const HoldConfirmModal = ({
  open,
  teamId,
  holdId,
  csrfToken,
  holdPositions,
  rooms,
  roomIds,
  venueOwnerships,
  eventTemplate,
  typedDateEntryEnabled,
  doorsTimeDefault,
  eventEndTimeDefault,
  eventTimeDefaults,
  onClose,
}) => {
  const [step, setStep] = useState(1);
  const [selectedHoldPositionId, setSelectedHoldPositionId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [doorTime, setDoorTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [userChangedDoorTime, setUserChangedDoorTime] = useState(false);
  const [userChangedEndTime, setUserChangedEndTime] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(eventTemplate || null);
  const [selectedRooms, setSelectedRooms] = useState(() => buildSelectedRooms(rooms, roomIds));
  const [clearHoldsOnDay, setClearHoldsOnDay] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedRoomIds = selectedRooms.map((r) => r.value);

  const dateTimePickerProps = (value, onDates) =>
    confirmDateTimePickerProps({
      typedEntry: typedDateEntryEnabled,
      dateFormat: flatPickrFormat,
      minDate: offsetDateForBrowser(new Date()),
      value,
      onDates,
      onInvalidEntry: toastError,
    });

  const changeStartTime = (start) => {
    setStartTime(start);
    const derived = resolveTimeDefaults(
      { start_time: start, door_time: doorTime, end_time: endTime },
      doorsTimeDefault,
      eventEndTimeDefault,
      eventTimeDefaults,
      !userChangedDoorTime,
      !userChangedEndTime
    );
    if (!derived) return;
    setDoorTime(derived.door_time || null);
    setEndTime(derived.end_time || null);
  };

  const canSubmit = canSubmitHoldConfirm({
    start_time: startTime,
    door_time: doorTime,
    end_time: endTime,
    selectedHoldPositionId,
    roomIds: selectedRoomIds,
  }) && !saving;

  const handleClose = () => {
    setStep(1);
    setSelectedHoldPositionId(null);
    setStartTime(null);
    setDoorTime(null);
    setEndTime(null);
    setUserChangedDoorTime(false);
    setUserChangedEndTime(false);
    setSelectedTemplate(eventTemplate || null);
    setSelectedRooms(buildSelectedRooms(rooms, roomIds));
    setClearHoldsOnDay(false);
    setSaving(false);
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios
      .post(`/teams/${teamId}/hold_groups/${holdId}/convert_to_confirm`, {
        confirm: {
          start_time: offsetDateForServer(startTime),
          door_time: offsetDateForServer(doorTime),
          end_time: offsetDateForServer(endTime),
          event_template_id: selectedTemplate ? selectedTemplate.value : null,
          new_room_ids: selectedRoomIds,
        },
        clear_holds_on_day: clearHoldsOnDay,
      })
      .then(({ data }) => {
        toastSuccess("Event confirmed!");
        window.location.href = `/teams/${teamId}/confirms/${data.id}`;
      })
      .catch((error) => {
        const msgs =
          error.response && error.response.data
            ? error.response.data.join(", ")
            : "Unable to confirm event. Please try again.";
        toastError(msgs);
        setSaving(false);
      });
  };

  const sortedPositions = [...holdPositions].sort(
    (a, b) => new Date(a.startTime) - new Date(b.startTime)
  );

  return (
    <Popup
      open={open}
      contentStyle={holdToConfirmModalContentStyle}
      closeOnDocumentClick
      className="popup-modal"
      onClose={handleClose}
    >
      <div className="popup-modal-container">
        <div
          className="row m-0"
          style={{ borderBottom: "1px solid #f2f2f2" }}
        >
          <div className="col" style={{ padding: "25px" }}>
            <p className="mb-0" style={{ fontSize: "20px" }}>
              <strong>Confirm Event</strong>
            </p>
          </div>
          <div className="col-xs-auto d-flex align-items-center">
            <a
              href="#"
              className="text-muted"
              style={{ paddingRight: "30px", fontSize: "20px" }}
              onClick={(e) => {
                e.preventDefault();
                handleClose();
              }}
            >
              <i className="fa fa-times"></i>
            </a>
          </div>
        </div>
        <div className="row m-0">
          <div className="col-12" style={{ padding: "18px 25px" }}>
            {step === 2 ? (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="new-event-template" className="text-muted">
                    Template
                    <span className="text-optional pl-2">(optional)</span>
                  </label>
                  <EventTemplateSelect
                    teamId={teamId}
                    id="new-event-template"
                    value={selectedTemplate}
                    onChange={setSelectedTemplate}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-event-rooms" className="text-muted">
                    Venue
                  </label>
                  <RoomSelector
                    rooms={rooms}
                    venueOwnerships={venueOwnerships}
                    selectedRooms={selectedRooms}
                    id="new-event-rooms"
                    menuPortalTarget={document.querySelector("body")}
                    onChange={(selected) => {
                      setSelectedRooms(selected || []);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="start_date_and_time" className="text-muted">
                    Event Start Date/Time
                  </label>
                  <div className="flatpickr-date-input px-2 d-flex align-items-center">
                    <div className="pr-2" style={{ fontSize: "18px" }}>
                      <i className="far fa-calendar"></i>
                    </div>
                    <Flatpickr
                      {...dateTimePickerProps(startTime, (dates) => changeStartTime(dates[0] || null))}
                      placeholder="Select date"
                      value={startTime}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="doors_date_and_time" className="text-muted">
                    Doors Date/Time
                  </label>
                  <div className="flatpickr-date-input px-2 d-flex align-items-center">
                    <div className="pr-2" style={{ fontSize: "18px" }}>
                      <i className="far fa-calendar"></i>
                    </div>
                    <Flatpickr
                      {...dateTimePickerProps(doorTime, (dates) => {
                        setUserChangedDoorTime(true);
                        setDoorTime(dates[0] || null);
                      })}
                      placeholder="Select date"
                      value={doorTime}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label htmlFor="end_date_and_time" className="text-muted">
                    Event End Date/Time
                  </label>
                  <div className="flatpickr-date-input px-2 d-flex align-items-center">
                    <div className="pr-2" style={{ fontSize: "18px" }}>
                      <i className="far fa-calendar"></i>
                    </div>
                    <Flatpickr
                      {...dateTimePickerProps(endTime, (dates) => {
                        setUserChangedEndTime(true);
                        setEndTime(dates[0] || null);
                      })}
                      placeholder="Select date"
                      value={endTime}
                    />
                  </div>
                </div>
                <div
                  className="custom-control custom-control-inline custom-checkbox custom-checkbox-table"
                  style={{ marginBottom: "20px", display: "flex", alignItems: "center" }}
                >
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    checked={clearHoldsOnDay}
                    onChange={(e) => setClearHoldsOnDay(e.target.checked)}
                    id="clear-other-holds"
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="clear-other-holds"
                    style={{ paddingTop: 2 }}
                  >
                    Clear all relevant holds on this day
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn btn-primary btn-block"
                >
                  {saving ? (
                    <React.Fragment>
                      <img
                        src="/uploading-loading.gif"
                        className="mr-1"
                        style={{ width: "16px" }}
                      />
                      <strong>Saving...</strong>
                    </React.Fragment>
                  ) : (
                    <strong>Save</strong>
                  )}
                </button>
              </form>
            ) : (
              <form>
                <p className="text-muted">Select the date you want to confirm.</p>
                {sortedPositions.map((holdPosition) => (
                  <div key={holdPosition.id} className="custom-control custom-radio">
                    <input
                      type="radio"
                      id={`confirm-hold-position-${holdPosition.id}`}
                      name="confirm-hold-position"
                      checked={selectedHoldPositionId === holdPosition.id}
                      onChange={() => {
                        setSelectedHoldPositionId(holdPosition.id);
                        changeStartTime(applyDefaultStart(eventTimeDefaults, offsetDateForBrowser(holdPosition.startTime)));
                      }}
                      className="custom-control-input"
                    />
                    <label
                      className="custom-control-label"
                      style={{ fontSize: "16px" }}
                      htmlFor={`confirm-hold-position-${holdPosition.id}`}
                    >
                      <strong>
                        {moment(offsetDateForBrowser(holdPosition.startTime)).format("MM/DD/YY")} -{" "}
                        {`H${holdPosition.position}`}
                      </strong>
                    </label>
                  </div>
                ))}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!selectedHoldPositionId) return false;
                    setStep(2);
                  }}
                  style={{ marginTop: "20px" }}
                  className={
                    "btn btn-primary btn-block " +
                    (selectedHoldPositionId ? "" : "disabled")
                  }
                >
                  <strong>Next</strong>
                </a>
              </form>
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
};

HoldConfirmModal.propTypes = {
  open: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  holdId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  csrfToken: PropTypes.string,
  holdPositions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      position: PropTypes.number,
      startTime: PropTypes.string,
    })
  ).isRequired,
  rooms: PropTypes.array,
  roomIds: PropTypes.array,
  venueOwnerships: PropTypes.array,
  typedDateEntryEnabled: PropTypes.bool,
  doorsTimeDefault: PropTypes.object,
  eventEndTimeDefault: PropTypes.object,
  eventTimeDefaults: PropTypes.object,
  eventTemplate: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default HoldConfirmModal;
